// src/pages/ResetPassword.js
import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, CheckCircle, Shield, Car, Navigation, ArrowRight } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import ThemeToggle from '../components/ui/ThemeToggle';
import { auth as authApi } from '../services/api';

function calcStrength(pw) {
  let score = 0;
  if (pw.length >= 8)                             score++;
  if (pw.length >= 12)                            score++;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw))      score++;
  if (/\d/.test(pw))                              score++;
  if (/[!@#$%^&*(),.?":{}|<>]/.test(pw))         score++;
  const levels = [
    { label: '',          color: '' },
    { label: 'Weak',      color: '#ef4444' },
    { label: 'Fair',      color: '#f97316' },
    { label: 'Good',      color: '#eab308' },
    { label: 'Strong',    color: '#22c55e' },
    { label: 'Excellent', color: '#00ACC1' },
  ];
  return { ...levels[Math.min(score, 5)], score };
}

export default function ResetPassword() {
  const theme    = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirm,  setConfirm]  = useState('');
  const [showPw,   setShowPw]   = useState(false);
  const [showCon,  setShowCon]  = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [done,     setDone]     = useState(false);
  const [loaded,   setLoaded]   = useState(false);

  useEffect(() => { const t = setTimeout(() => setLoaded(true), 80); return () => clearTimeout(t); }, []);

  const strength       = calcStrength(password);
  const passwordsMatch = password && confirm && password === confirm;

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (password.length < 8)  { setError('Password must be at least 8 characters'); return; }
    if (password !== confirm)  { setError('Passwords do not match'); return; }
    setLoading(true);
    const res = await authApi.reset(token, password);
    setLoading(false);
    if (res.success) { setDone(true); setTimeout(() => navigate('/login'), 3000); }
    else setError(res.error || 'Reset failed. The link may have expired.');
  }

  const inputStyle = { backgroundColor: theme.background, borderColor: theme.border, color: theme.text };

  return (
    <div className="fixed inset-0 overflow-y-auto" style={{ backgroundColor: theme.background }}>

      {/* Blobs */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute w-[600px] h-[600px] rounded-full blur-3xl opacity-20 -top-32 -right-32"
          style={{ background: 'radial-gradient(circle, #4CAF50, transparent)' }} />
        <div className="absolute w-[500px] h-[500px] rounded-full blur-3xl opacity-15 -bottom-20 -left-20"
          style={{ background: 'radial-gradient(circle, #00BCD4, transparent)' }} />
      </div>

      {/* Centered layout */}
      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-6 py-10">

        {/* Card */}
        <div className="w-full max-w-[400px] rounded-3xl p-8 transition-all duration-700"
          style={{
            backgroundColor: theme.card,
            backdropFilter: 'blur(24px)',
            border: `1px solid ${theme.border}`,
            boxShadow: theme.isDark ? '0 24px 80px rgba(0,0,0,0.5)' : '0 24px 80px rgba(0,0,0,0.1)',
            transform: loaded ? 'translateY(0) scale(1)' : 'translateY(24px) scale(0.97)',
            opacity: loaded ? 1 : 0,
          }}>

          {/* Logo + toggle row */}
          <div className="flex items-center justify-between mb-7">
            <img src="/logo.png" alt="TripData" className="h-7 object-contain" />
            <ThemeToggle />
          </div>

          {!token ? (
            <>
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5"
                style={{ backgroundColor: theme.isDark ? 'rgba(239,68,68,0.15)' : '#fef2f2' }}>
                <Shield size={22} color="#f87171" />
              </div>
              <h1 className="text-2xl font-bold mb-2 text-center" style={{ color: theme.text }}>Invalid link</h1>
              <p className="text-sm mb-5" style={{ color: theme.textMuted }}>This reset link is missing or invalid.</p>
              <Link to="/forgot-password" className="flex items-center gap-1.5 text-sm font-semibold" style={{ color: '#00ACC1' }}>
                <ArrowRight size={14} />Request a new link
              </Link>
            </>
          ) : done ? (
            <>
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5"
                style={{ background: 'linear-gradient(135deg, #00BCD4, #4CAF50)' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold mb-2 text-center" style={{ color: theme.text }}>Password updated</h1>
              <p className="text-sm" style={{ color: theme.textMuted }}>Redirecting you to sign in...</p>
            </>
          ) : (
            <>
              <div className="w-12 h-12 rounded-2xl mb-5 mx-auto flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #00BCD4, #4CAF50)' }}>
                <Navigation size={22} color="white" />
              </div>
              <h1 className="text-2xl font-bold mb-1 text-center" style={{ color: theme.text }}>Set new password</h1>
              <p className="text-sm mb-7 text-center" style={{ color: theme.textMuted }}>Choose a strong password for your account.</p>

              {error && (
                <div className="mb-5 px-4 py-3 rounded-2xl text-sm"
                  style={{ backgroundColor: theme.isDark ? 'rgba(239,68,68,0.12)' : '#fef2f2', color: '#f87171', border: '1px solid rgba(248,113,113,0.25)' }}>
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: theme.textMuted }}>New Password</label>
                  <div className="relative">
                    <input type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                      required autoComplete="new-password" placeholder="Min 8 characters"
                      className="w-full px-4 py-3 pr-11 rounded-2xl text-sm outline-none transition-all border"
                      style={inputStyle} />
                    <button type="button" onClick={() => setShowPw(!showPw)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 hover:opacity-70 transition-opacity"
                      style={{ color: theme.textMuted }}>
                      {showPw ? <EyeOff size={17} /> : <Eye size={17} />}
                    </button>
                  </div>
                  {password.length > 0 && (
                    <div className="mt-2 flex items-center gap-2">
                      <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: theme.border }}>
                        <div className="h-full rounded-full transition-all duration-300"
                          style={{ width: `${(strength.score / 5) * 100}%`, backgroundColor: strength.color }} />
                      </div>
                      <span className="text-xs font-semibold w-16 text-right" style={{ color: strength.color }}>{strength.label}</span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: theme.textMuted }}>Confirm Password</label>
                  <div className="relative">
                    <input type={showCon ? 'text' : 'password'} value={confirm} onChange={e => setConfirm(e.target.value)}
                      required autoComplete="new-password" placeholder="••••••••"
                      className="w-full px-4 py-3 pr-11 rounded-2xl text-sm outline-none transition-all border"
                      style={inputStyle} />
                    <button type="button" onClick={() => setShowCon(!showCon)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 hover:opacity-70 transition-opacity"
                      style={{ color: theme.textMuted }}>
                      {showCon ? <EyeOff size={17} /> : <Eye size={17} />}
                    </button>
                  </div>
                  {passwordsMatch && (
                    <div className="mt-1.5 flex items-center gap-1.5" style={{ color: '#22c55e' }}>
                      <CheckCircle size={13} /><span className="text-xs font-medium">Passwords match</span>
                    </div>
                  )}
                </div>

                <button type="submit" disabled={loading}
                  className="w-full py-3.5 rounded-2xl text-white font-bold text-sm transition-all disabled:opacity-60 flex items-center justify-center gap-2 mt-2"
                  style={{ background: 'linear-gradient(135deg, #00BCD4, #4CAF50)', boxShadow: '0 8px 32px rgba(0,172,193,0.35)' }}>
                  {loading ? 'Updating...' : <><span>Update password</span><ArrowRight size={16} /></>}
                </button>
              </form>
            </>
          )}
        </div>

        {/* Back to website */}
        <a href="https://tripdata.co.za"
          className="flex items-center gap-1.5 mt-5 text-xs font-medium transition-opacity hover:opacity-70"
          style={{ color: theme.textMuted }}>
          <ArrowRight size={12} className="rotate-180" />
          Back to tripdata.co.za
        </a>
      </div>

    </div>
  );
}